import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useProject} from "../../providers/ClientProjectProvider";
import {useAuth} from "../../providers/AuthProvider";
import AnimatedNumber from "animated-number-react";
import PositionCandidateGreenlightModal from "./PositionCandidateGreenlightModal";
import {InputNumber, Modal} from "antd";
import Form, {FormInputComponent} from "../../components/form/Form";
import {usePositionOverride} from "../../providers/PositionOverrideProvider";
import {usePosition} from "../../providers/ProjectPositionProvider";
import InfoTooltip from "../../components/InfoTooltip";
import TalentCandidate from "../../models/TalentCandidate";
import PositionTimeline from "../project-position/components/PositionTimeline";
import {useLocation, useSearchParams} from "react-router-dom";

/**
 * PositionOverrideInput Component
 *
 * A reusable input component for editing position override values.
 * Provides consistent UI patterns for editable fields with labels and validation.
 */
const PositionOverrideInput = ({
                                   onEdit,
                                   isEditing,
                                   canEdit,
                                   label,
                                   displayValue,
                                   isInvalid,
                                   formClass = '',
                                   ...props
                               }) => {

    const {
        assignedModel,
        isLoading,
        isDisabled,
        onChange,
        onReset,
    } = usePositionOverride();

    const formRef = useRef(null);

    return (
        <div className="w-full flex-grow relative">
            <div className="pb-2 flex flex-row items-center gap-1.5">
                <p className="inline font-semibold text-xs text-light-gray4">
                    {label}
                </p>
                {canEdit && (
                    <button type="button" disabled={isDisabled} onClick={onEdit}
                            className="btn btn-link-primary no-underline hover:underline focus:underline py-0 disabled:opacity-60 disabled:no-underline">
                        <i className="fas fa-pen underline"/>
                    </button>
                )}
            </div>

            {isEditing ? (
                <div className={"flex flex-col position-cost-form " + formClass}>
                    {props.children}
                </div>
            ) : (
                <p>{displayValue}</p>
            )}
        </div>
    )
}

/**
 * PositionCandidateFixedRetainerCostOverrideTab Component
 *
 * This component handles cost override functionality for time & materials projects.
 * It provides comprehensive cost management including bill rates, markups, hours,
 * and timing configurations.
 *
 * Key Features:
 * - Bill rate and premarkup rate editing (if hourly based)
 * - Talent Payout (if cost-based)
 * - Talent markup percentage configuration
 * - Total periods and average hours per period management
 * - Project timing and date management
 * - Real-time cost calculations and animations
 * - Tax classification modal integration
 *
 * Usage: This component should only be used when project.isFixedFee() returns false
 */
const PositionCandidateFixedRetainerCostOverrideTab = ({...props}) => {

    const {auth} = useAuth();
    const {project} = useProject();
    const {position} = usePosition();
    const {
        assignedModel,
        isLoading,
        isDisabled: isPositionOverideDisabled,
        updateContractTerms,
        handlePositionUpdated,
    } = usePositionOverride();

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams()

    const formRef = useRef();

    const [isSaving, setIsSaving] = useState(isPositionOverideDisabled);
    const isDisabled = isPositionOverideDisabled || isSaving
    const [formModel, setFormModel] = useState(assignedModel)
    const canEditProjectAndPositions = auth?.canEditProjectAndPositions(project);
    const canAddRemoveProjectPositions = auth?.canAddRemoveProjectPositions(project);
    const isConsultant = auth?.isClientConsultant() || auth.canManageClients();

    // Disabled if user is not client lead or if project is active and has change orders
    const editDisabled = !isConsultant || !canEditProjectAndPositions || project.is_on_hold ||
        (project.isActive() && project.change_orders?.length)

    const candidate = useMemo(() => {
        return position.assignedCandidate();
    }, [
        position?.id,
        isDisabled,
        position?.is_filled,
        position?.assigned_candidate?.tax_classify_status,

        position?.assigned_candidate?.job_candidate?.confirmed_avg_hours_per_period,
        position?.assigned_candidate?.job_candidate?.confirmed_started_at,
        position?.assigned_candidate?.job_candidate?.confirmed_ended_at,

        position?.assigned_candidate?.job_candidate?.platform_cost,
        position?.assigned_candidate?.job_candidate?.premarkup_rate,
        position?.assigned_candidate?.job_candidate?.total_periods,
        position?.assigned_candidate?.job_candidate?.platform_cost,
        position?.assigned_candidate?.job_candidate?.contract_value,
        position?.assigned_candidate?.job_candidate?.total_payout_amount,
        position?.assigned_candidate?.job_candidate?.payout_amount,
        position?.assigned_candidate?.job_candidate?.exact_platform_rate,
        position?.assigned_candidate?.job_candidate?.platform_rate,
        position?.assigned_candidate?.job_candidate?.percent_markup,

        position?.platform_cost
    ]);

    const [activeEdit, setActiveEdit] = useState(null);

    const onEditSection = async (section) => {
        if (editDisabled) return;
        await setFormModel(assignedModel);
        await setActiveEdit(section);
        await formRef.current?.forceReset(assignedModel)
    }

    const onSaveSection = async (model = formModel) => {
        if (editDisabled) return;

        let updatedFields = []
        switch (activeEdit) {
            case 'timing':
                updatedFields = ['confirmed_ended_at', 'confirmed_started_at']
                break;
            case 'total_periods':
                updatedFields = ['total_periods']
                break;
            case 'confirmed_avg_hours_per_period':
                updatedFields = ['confirmed_avg_hours_per_period']
                break;
            case 'payout_amount':
                updatedFields = ['payout_amount']
                break;
            case 'platform_cost':
                updatedFields = ['percent_markup']
                break;
            case 'exact_platform_rate':
                updatedFields = ['exact_platform_rate']
                break;
            case 'premarkup_rate':
                updatedFields = ['premarkup_rate']
                break;
            case 'display_percent_markup':
                updatedFields = ['percent_markup']
                break;
        }

        if (isConsultant && candidate) {
            await setIsSaving(true)
            let updatedPosition = await updateContractTerms(TB.pick(model, updatedFields));

            if (updatedPosition) {
                let updatedModel = {...updatedPosition.assigned_candidate?.job_candidate}
                if (updatedModel.confirmed_started_at) {
                    updatedModel.confirmed_started_at = TB.moment(updatedModel.confirmed_started_at)
                }
                if (updatedModel.confirmed_ended_at) {
                    updatedModel.confirmed_ended_at = TB.moment(updatedModel.confirmed_ended_at)
                }
                updatedModel.display_percent_markup = Number((parseFloat((updatedModel.percent_markup)) * 100).toFixed(4));
                updatedModel.total_periods = updatedPosition.actual_total_periods;
                updatedModel.payout_amount = updatedPosition.isFixedRetainerCostPerPeriodBased() ? updatedModel.talent_opps?.[0]?.payout_amount :
                    (updatedModel.estimated_payout_amount / updatedModel.total_periods)
                updatedModel.platform_cost = (updatedModel.payout_amount) * (1 + (parseFloat((updatedModel.percent_markup))));
                updatedModel.total_payout_amount = updatedModel.estimated_payout_amount;
                updatedModel.contract_value = updatedModel.exact_platform_cost;
                TB.toast('Candidate Updated Successfully', {theme: 'success'});

                await setFormModel(TalentCandidate.make({...updatedModel}));
                await formRef.current?.forceReset(updatedModel)
            }
        }
        await setIsSaving(false)
        setActiveEdit(null);
    }

    // Calculate client bill rate for time & materials projects
    const clientBillRate = useMemo(() => {
        return `$${formModel.exact_platform_rate}`
    }, [formModel.exact_platform_rate]);

    const originalStandardMarkup = useMemo(() => {
        return Number(parseFloat(formModel.standard_percent_markup).toFixed(2) * 100)
    }, [formModel.standard_percent_markup]);

    const [bulkCost, setBulkCost] = useState(position?.assigned_candidate?.job_candidate?.platform_cost || position.exact_platform_cost);

    const [talentPayout, setTalentPayout] = useState(position?.assigned_candidate?.job_candidate?.exact_premarkup_cost || position.exact_premarkup_cost);

    const [duration, setDuration] = useState(0);

    useEffect(() => {
        setDuration(1000);
        setBulkCost(position?.assigned_candidate?.job_candidate?.exact_platform_cost || position.exact_platform_cost);
        setTalentPayout(position?.assigned_candidate?.job_candidate?.exact_premarkup_cost || position.exact_premarkup_cost);
    }, [position.exact_platform_cost]);

    const toggleCandidateTaxClassificationModal = async () => {
        if (isDisabled) {
            return;
        }
        const updatedParams = new URLSearchParams(searchParams);
        if (updatedParams.has('tax-classification') && parseInt(updatedParams.get('tax-classification')) === position.id) {
            updatedParams.delete('tax-classification');
        }else {
            updatedParams.set('tax-classification', position.id);
        }
        return setSearchParams(updatedParams)
    }

    const startMinDate = useMemo(() => {
        let minDate = undefined;
        return minDate;
    }, [formModel.start_date]);

    const onFormChange = async (params) => {
        if (editDisabled) return;
        let update = {...formModel};
        let formUpdates = {};
        for (let [key, value] of Object.entries(params)) {
            update = TB.set(update, key, value);
        }

        // Reset end date if start date changes moves ahead
        if (params.confirmed_ended_at || params.confirmed_started_at) {
            let updatedEndDate = TB.moment(update.confirmed_ended_at);
            let currentStartDate = TB.moment(update.confirmed_started_at || formModel.confirmed_started_at);
            if (!updatedEndDate.isValid() || TB.moment(currentStartDate)
                .isSameOrAfter(updatedEndDate, 'day')) {
                update.confirmed_ended_at = null;
                formUpdates.confirmed_ended_at = null
            }
        }

        if (position.isFixedRetainerRateBased()) {
            if (params.premarkup_rate && formModel.premarkup_rate !== params.premarkup_rate) {
                update.exact_premarkup_rate = params.premarkup_rate;
                update.exact_platform_rate = Number((update.exact_premarkup_rate * (1 + update.percent_markup)).toFixed(6))
                formUpdates.exact_platform_rate = update.exact_platform_rate;
            }
            if (params.exact_platform_rate && formModel.exact_platform_rate !== params.exact_platform_rate) {
                update.percent_markup = Number(parseFloat(
                    (params.exact_platform_rate - formModel.premarkup_rate) / formModel.premarkup_rate
                )).toFixed(6);

                update.display_percent_markup = Number(parseFloat((update.percent_markup)) * 100).toFixed(6);

                formUpdates.display_percent_markup = update.display_percent_markup;
            }
        }else {
            if (params.payout_amount && formModel.payout_amount !== params.payout_amount) {
                update.payout_amount = params.payout_amount;
                update.total_payout_amount = Number((update.payout_amount * update.total_periods))
                update.platform_cost = update.payout_amount * (1 + update.percent_markup)
                update.contract_value = update.total_payout_amount * (1 + update.percent_markup)
            }
            if (params.platform_cost && formModel.platform_cost !== params.platform_cost) {
                update.platform_cost = params.platform_cost;

                update.percent_markup = Number(parseFloat(
                    (params.platform_cost - formModel.payout_amount) / formModel.payout_amount
                )).toFixed(6);

                update.contract_value = update.total_payout_amount * (1 + update.percent_markup)

                update.display_percent_markup = Number(parseFloat((update.percent_markup)) * 100).toFixed(6);

                formUpdates.display_percent_markup = update.display_percent_markup;
            }
        }

        if (params.display_percent_markup && formModel.display_percent_markup !== params.display_percent_markup) {

            update.percent_markup = Number(parseFloat(params.display_percent_markup) / 100);
            console.log('onFormChange', params.display_percent_markup)

            if (position.isFixedRetainerRateBased()) {
                update.exact_platform_rate = Number((update.exact_premarkup_rate * (1 + update.percent_markup)).toFixed(6))
                formUpdates.exact_platform_rate = update.exact_platform_rate;
            }else {
                // update.platform_cost_per_period = params.payout_amount_per_period * (1 + update.percent_markup);
                update.platform_cost = update.payout_amount * (1 + update.percent_markup)
                update.contract_value = candidate.platform_cost * candidate.total_periods
            }
        }

        if (update.confirmed_started_at) {
            update.confirmed_started_at = TB.moment(update.confirmed_started_at)
        }
        if (update.confirmed_ended_at) {
            update.confirmed_ended_at = TB.moment(update.confirmed_ended_at)
        }
        update.display_percent_markup = Number((parseFloat((update.percent_markup)) * 100).toFixed(6));

        console.log('onFormChange', update)
        await setFormModel(TalentCandidate.make(update))
        if (Object.keys(formUpdates).length > 0 && formRef.current) {
            return formRef.current.updateInputsWithValue(formUpdates)
        }
    }

    return (
        <React.Fragment>
            <Form
                ref={formRef}
                className={"position-cost-form-container"}
                onSubmit={() => onSaveSection()}
                submitButton={false}
                background={"none"}
                isDisabled={isDisabled}
                model={formModel}
                onChange={onFormChange}
                fields={[]}>
                <div className="flex flex-col gap-3 sm:gap-5 xs:flex-row items-stretch xs:items-start mb-2">
                    {/* Left Column - Bill Rate and Markup Configuration */}
                    <div className="flex xs:flex-col gap-6 flex-1">
                        {position.isFixedRetainerRateBased() ? (
                            <div className="w-full flex-grow pt-3">
                                {/* Assigned Bill Rate Section */}
                                <PositionOverrideInput
                                    label={(
                                        <React.Fragment>
                                            <span className="text-info">Assigned Bill Rate:</span>
                                            <i id={`assigned-rate-note-${position?.id}-${formModel.id}`}
                                               className="fas fa-info-circle text-black ml-1"/>
                                            <InfoTooltip target={`assigned-rate-note-${position?.id}-${formModel.id}`}
                                                         type="dark" placement="top">
                                                <span className="text-white">
                                                    This is the bill rate that the client sees. Based on a <b>{formModel.display_percent_markup}</b>% markup of talent rate
                                                </span>
                                            </InfoTooltip>
                                        </React.Fragment>
                                    )}
                                    isEditing={activeEdit === 'exact_platform_rate'}
                                    canEdit={!activeEdit && !editDisabled}
                                    onEdit={() => onEditSection('exact_platform_rate')}
                                    displayValue={(
                                        <React.Fragment>
                                            <span className="flex flex-col">
                                                <span>{clientBillRate} / hour</span>
                                                {!position.hasBeenFilled() && !position.target_rate && (
                                                    <span className="whitespace-no-wrap">
                                                        <small>* Bill rate set based upon average rate
                                                            on {laravel.platform_name}</small>
                                                    </span>
                                                )}
                                            </span>
                                        </React.Fragment>
                                    )}
                                >
                                    <div className="flex flex-col items-start">
                                        <FormInputComponent
                                            fieldName="exact_platform_rate"
                                            value={formModel.exact_platform_rate}
                                            type="antd-input"
                                            disabled={isDisabled}
                                            placeholder="--"
                                            min={1}
                                            max={5000}
                                            label={false}
                                            required={true}
                                            validation={{
                                                rules: {
                                                    min(values, value) {
                                                        return parseFloat(value) >= formModel.premarkup_rate
                                                    },
                                                    max(values, value) {
                                                        if (!TB.isDefined(value)) return true;
                                                        return parseFloat(value) <= 5000;
                                                    },
                                                },
                                                errors: {
                                                    min: `The bill rate must be greater than the talent premarkup rate`,
                                                    max: `The bill rate must be less than $5000`,
                                                }
                                            }}
                                        >
                                            <InputNumber
                                                precision={2}
                                                controls={false}
                                                className="w-full"
                                                addonBefore="$"
                                                addonAfter=" per hour "
                                            />
                                        </FormInputComponent>
                                    </div>
                                </PositionOverrideInput>
                            </div>
                        ) : (
                            <div className="w-full flex-grow pt-3">
                                {/* Assigned Bill Rate Section */}
                                <PositionOverrideInput
                                    label={(
                                        <React.Fragment>
                                            <span className="text-info">{TB.capitalize(position.payout_frequency)} Billed Retainer</span>
                                            <i id={`assigned-rate-note-${position?.id}-${formModel.id}`}
                                               className="fas fa-info-circle text-black ml-1"/>
                                            <InfoTooltip target={`assigned-rate-note-${position?.id}-${formModel.id}`}
                                                         type="dark" placement="top">
                                                <span className="text-white">
                                                    This is the proposed {position.payout_frequency} retainer that the client sees. Based on a <b>{formModel.display_percent_markup}</b>% markup of talent payout
                                                </span>
                                            </InfoTooltip>
                                        </React.Fragment>
                                    )}
                                    isEditing={activeEdit === 'platform_cost'}
                                    canEdit={!activeEdit && !editDisabled}
                                    onEdit={() => onEditSection('platform_cost')}
                                    displayValue={(
                                        <React.Fragment>
                                            <span className="flex flex-col">
                                                <span>
                                                    ${Number(formModel.platform_cost).toFixed(2)} /{position.payout_frequency === 'weekly' ? 'wk' : 'mth'}
                                                </span>
                                            </span>
                                        </React.Fragment>
                                    )}
                                >
                                    <div className="flex flex-col items-start">
                                        <FormInputComponent
                                            fieldName="platform_cost"
                                            value={formModel.platform_cost}
                                            type="antd-input"
                                            disabled={isDisabled}
                                            placeholder="--"
                                            min={0}
                                            max={1000000}
                                            label={false}
                                            required={true}
                                            validation={{
                                                rules: {
                                                    min(values, value) {
                                                        return parseFloat(value) >= 0
                                                    },
                                                    max(values, value) {
                                                        if (!TB.isDefined(value)) return true;
                                                        return parseFloat(value) <= 1000000;
                                                    },
                                                },
                                                errors: {
                                                    min: `The payout amount per ${position.payout_frequency === 'weekly' ? 'week' : 'month'} must be greater than $0`,
                                                    max: `The payout amount per ${position.payout_frequency === 'weekly' ? 'week' : 'month'} rate must be less than $1,000,000`,
                                                }
                                            }}
                                        >
                                            <InputNumber
                                                precision={2}
                                                controls={false}
                                                className="w-full"
                                                addonBefore="$"
                                            />
                                        </FormInputComponent>
                                    </div>
                                </PositionOverrideInput>
                            </div>
                        )}

                        {/* Talent Percent Markup Section */}
                        <div className="w-full flex-grow">
                            <PositionOverrideInput
                                label="Talent Percent Markup"
                                isEditing={activeEdit === 'display_percent_markup'}
                                canEdit={!activeEdit && !editDisabled}
                                onEdit={() => onEditSection('display_percent_markup')}
                                displayValue={(
                                    <React.Fragment>
                                    <span className="flex flex-row items-center gap-3">
                                        {originalStandardMarkup !== formModel.display_percent_markup && (
                                            <span className="text-danger line-through">{originalStandardMarkup}%</span>
                                        )}
                                        <span>{formModel.display_percent_markup}%</span>
                                    </span>
                                    </React.Fragment>
                                )}
                            >
                                <div className="flex flex-row items-center">
                                    <div className="w-[130px]">
                                        <FormInputComponent
                                            fieldName="display_percent_markup"
                                            value={formModel.display_percent_markup}
                                            type="antd-input"
                                            disabled={isDisabled}
                                            placeholder="--"
                                            min={0}
                                            label={false}
                                            required={true}
                                        >
                                            <InputNumber
                                                controls={false}
                                                className="w-full"
                                                addonAfter="%"
                                                min="0"
                                                max="5000"
                                                precision={6}
                                                formatter={value => Number(value)}
                                            />
                                        </FormInputComponent>
                                    </div>
                                    {originalStandardMarkup !== formModel.display_percent_markup && (
                                        <div className="mx-2 mb-4 flex-grow">
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    await onFormChange({display_percent_markup: originalStandardMarkup});
                                                    return onSaveSection({
                                                        display_percent_markup: originalStandardMarkup,
                                                        percent_markup: Number(parseFloat(originalStandardMarkup) / 100)
                                                    })
                                                }}
                                                className="btn btn-sm btn-link mx-auto btn-link-danger">
                                                <small>Reset to standard markup: {originalStandardMarkup}%</small>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </PositionOverrideInput>
                        </div>
                        {/* Total Hours Section */}

                        {position.isFixedRetainerRateBased() && (
                            <div className="w-full flex-grow">
                                <PositionOverrideInput
                                    label={<span>Talent Total Hours Budgeted:</span>}
                                    canEdit={!activeEdit && !editDisabled}
                                    isEditing={false}
                                    onEdit={() => true}
                                    displayValue={`${parseFloat(Number(formModel?.confirmed_avg_hours_per_period * formModel.total_periods).toFixed(6))} hours`}>

                                </PositionOverrideInput>
                            </div>
                        )}
                        <div className="w-full flex-grow">
                            <PositionOverrideInput
                                // label={formModel.total_periods === position.estimated_total_periods ? (
                                //     <span>Total {position.payoutFrequency()} Budgeted:</span>
                                // ) : (
                                //     <span className="text-danger">Talent Total {position.payoutFrequency()} Override:
                                //         {activeEdit && (
                                //             <InfoTooltip placement="top" type="dark" iconColor="black">
                                //                 <span className="text-left text-white">
                                //                     This value overrides the total estimated {position.payoutFrequency()}.
                                //                 </span>
                                //             </InfoTooltip>
                                //         )}
                                //     </span>
                                // )}
                                label={<span>Total {TB.capitalize(position.payoutFrequency())} Budgeted:</span>}
                                canEdit={!activeEdit && !editDisabled}
                                isEditing={activeEdit === 'total_periods'}
                                onEdit={() => onEditSection('total_periods')}
                                displayValue={`${formModel?.total_periods} ${position.payoutFrequency()}`}>

                                <FormInputComponent
                                    fieldName="total_periods"
                                    value={formModel?.total_periods}
                                    label={false}
                                    type="antd-input"
                                    disabled={isDisabled}
                                    required={false}
                                    min={1}>
                                    <InputNumber
                                        controls={false}
                                        precision={6}
                                        className="w-full"
                                        addonAfter={` ${position.payoutFrequency()}`}
                                        required={false}/>
                                </FormInputComponent>
                            </PositionOverrideInput>
                        </div>
                    </div>

                    {/* Middle Column - Timing and Rate Details */}
                    <div className="flex xs:flex-col gap-6 flex-1 bg-light-gray3 p-3">
                        {/* Talent Confirmed Dates Section */}
                        <PositionOverrideInput
                            label="Talent Confirmed Start Date:"
                            canEdit={!activeEdit && !editDisabled}
                            isEditing={activeEdit === 'timing'}
                            onEdit={() => onEditSection('timing')}
                            displayValue={TB.moment(formModel.confirmed_started_at).format('ll')}>
                            <div className="flex flex-row items-start gap-3">
                                <FormInputComponent
                                    fieldName="confirmed_started_at"
                                    value={TB.moment(formModel.confirmed_started_at)}
                                    label={false}
                                    type="date"
                                    disabled={isDisabled}
                                    validationErrors={{
                                        isDefaultRequiredValue: TB.trans('validation.required', {
                                            'attribute': 'start date',
                                        })
                                    }}
                                    minDate={startMinDate}
                                    required={true}
                                />
                            </div>
                        </PositionOverrideInput>

                        {position.isFixedRetainerRateBased() ? (
                            <React.Fragment>
                                {/* Talent Confirmed Premarkup Rate Section */}
                                <PositionOverrideInput
                                    label="Talent Confirmed Premarkup Rate:"
                                    isEditing={activeEdit === 'premarkup_rate'}
                                    canEdit={!activeEdit && !editDisabled}
                                    onEdit={() => onEditSection('premarkup_rate')}
                                    displayValue={`$${formModel.exact_premarkup_rate} / hour`}
                                >
                                    <FormInputComponent
                                        fieldName="premarkup_rate"
                                        value={formModel.premarkup_rate}
                                        type="antd-input"
                                        disabled={isDisabled}
                                        placeholder="--"
                                        min={1}
                                        max={5000}
                                        label={false}
                                        required={true}
                                        validation={{
                                            rules: {
                                                isInt: true,
                                                min(values, value) {
                                                    if (!TB.isDefined(value)) return true;
                                                    return parseInt(value) >= 0
                                                },
                                                max(values, value) {
                                                    if (!TB.isDefined(value)) return true;
                                                    return parseInt(value) <= 5000;
                                                },
                                            },
                                            errors: {
                                                min: `The bill rate must be greater than $0.00`,
                                                max: `The bill rate must be less than $5000`,
                                            }
                                        }}
                                    >
                                        <InputNumber
                                            controls={false}
                                            className="w-full"
                                            addonBefore="$"
                                            addonAfter=" per hour "
                                        />
                                    </FormInputComponent>
                                </PositionOverrideInput>

                                {/* Talent Confirmed Hours per Week Section */}
                                <PositionOverrideInput
                                    label={                                        <span>
                                        Talent Est Hours per {position.payout_frequency === 'weekly' ? 'Week' : 'Month'}:

                                    </span>
                                    }
                                    isEditing={activeEdit === 'confirmed_avg_hours_per_period'}
                                    canEdit={!activeEdit && !editDisabled}
                                    onEdit={() => onEditSection('confirmed_avg_hours_per_period')}
                                    displayValue={`${formModel.confirmed_avg_hours_per_period} ${TB.pluralize('hr', parseInt(formModel.confirmed_avg_hours_per_period))} / ${position.payout_frequency === 'weekly' ? 'week':'month'}`}>

                                    <FormInputComponent
                                        fieldName="confirmed_avg_hours_per_period"
                                        value={formModel.confirmed_avg_hours_per_period}
                                        label={false}
                                        type="antd-input"
                                        disabled={isDisabled}
                                        required={true}
                                        min={1}
                                    >
                                        <InputNumber
                                            controls={false}
                                            precision={4}
                                            className="w-full"
                                            addonAfter={` hrs / ${position.payout_frequency === 'weekly' ? 'week' : 'month'} `}
                                            required={true}/>

                                    </FormInputComponent>
                                </PositionOverrideInput>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {/* Talent Confirmed Payout Amount Section */}
                                <PositionOverrideInput
                                    label={`Talent Confirmed ${TB.capitalize(position.payout_frequency)} Payout Amount:`}
                                    isEditing={activeEdit === 'payout_amount'}
                                    canEdit={!activeEdit && !editDisabled}
                                    onEdit={() => onEditSection('payout_amount')}
                                    displayValue={`$${Number(formModel.payout_amount).toFixed(2)} /${position.payout_frequency === 'weekly' ? 'wk' : 'mth'}`}
                                >
                                    <FormInputComponent
                                        fieldName="payout_amount"
                                        value={formModel.payout_amount}
                                        type="antd-input"
                                        disabled={isDisabled}
                                        placeholder="--"
                                        min={1}
                                        max={5000}
                                        label={false}
                                        required={true}
                                        validation={{
                                            rules: {
                                                // isInt: true,
                                                min(values, value) {
                                                    if (!TB.isDefined(value)) return true;
                                                    return parseInt(value) >= 0
                                                },
                                                max(values, value) {
                                                    if (!TB.isDefined(value)) return true;
                                                    return parseInt(value) <= 1000000;
                                                },
                                            },
                                            errors: {
                                                min: `The talent payout must be greater than $0.00`,
                                                max: `The talent payout must be less than $1,000,000`,
                                            }
                                        }}
                                    >
                                        <InputNumber
                                            controls={false}
                                            precision={2}
                                            className="w-full"
                                            addonBefore="$"
                                        />
                                    </FormInputComponent>
                                </PositionOverrideInput>
                            </React.Fragment>
                        )}
                    </div>

                    {/* Right Column - Cost Summary */}
                    <div className="flex-1">
                        <div className="p-3 bg-light-gray3 flex flex-col gap-6">
                            <div>
                                <p className="pb-2 font-semibold text-xs text-light-gray4">
                                    Total Estimated Client Fee
                                </p>
                                <AnimatedNumber
                                    value={bulkCost}
                                    formatValue={(value) => TB.formatCurrency(value, false)}
                                    duration={duration}
                                />
                            </div>
                            <div>
                                <p className="pb-2 font-semibold text-xs text-light-gray4">
                                    Total Estimated Talent Payout
                                </p>
                                <AnimatedNumber
                                    value={talentPayout}
                                    formatValue={(value) => TB.formatCurrency(value, false)}
                                    duration={duration}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Position Timeline with Edit Controls */}
                <PositionTimeline>
                    <div className={TB.mapClass("h-0 transition-all ease-in overflow-hidden", {
                        "h-[37px]": activeEdit
                    })}>
                        {activeEdit && (
                            <div className="flex flex-row gap-2 items-center justify-end">
                                <button type="button" disabled={isDisabled}
                                        onClick={() => onEditSection(null)}
                                        className="btn btn-dark disabled:opacity-60">
                                    Cancel
                                </button>
                                <button type="submit"
                                        disabled={isDisabled}
                                        className="btn btn-primary disabled:opacity-60">
                                    Update
                                </button>
                            </div>
                        )}
                    </div>
                </PositionTimeline>
            </Form>

            {/* Tax Classification Modal */}
            <Modal
                okText="Submit"
                title={position.client_tax_preference ? "Submit Tax Classification Preference" : "Tax Classification Status"}
                open={searchParams.has('tax-classification') && parseInt(searchParams.get('tax-classification')) === position.id}
                closable={true}
                maskClosable={false}
                style={{top: 50}}
                destroyOnClose={true}
                onCancel={toggleCandidateTaxClassificationModal}
                width={1200}
                footer={null}
            >
                {position && searchParams.has('tax-classification') && parseInt(searchParams.get('tax-classification')) === position.id && (
                    <PositionCandidateGreenlightModal
                        onClose={toggleCandidateTaxClassificationModal}
                    />
                )}
            </Modal>
        </React.Fragment>
    );
};

PositionCandidateFixedRetainerCostOverrideTab.propTypes = {};

export default PositionCandidateFixedRetainerCostOverrideTab;